import './UserManagement.scss';

import {
    Autocomplete,
    Breadcrumbs,
    Button,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { OktaAuth } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import { FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import Util from '../Util';
import Backend, {
    ScopUser_new,
    UserManagementCohortTypeResponse,
    UserManagementProjectResponse,
    UserManagementUserResponse,
} from '../backend/Backend';
import SiDialog from '../components/SiDialog';
import AquantCancelIcon from '../resources/images/aq-cancel.svg';
import { ReactComponent as DeleteIcon } from '../resources/images/delete.svg';
import { ReactComponent as ConfigEditIcon } from '../resources/images/edit_config.svg';

// ********* Backend actions: **************
const getUsersAndProjects = async (oktaAuth: OktaAuth) => Backend.getUsersAndProjects(oktaAuth);

const getScopUsers = async (oktaAuth: OktaAuth) => Backend.getScopUsers(oktaAuth);

const deleteScopUser = async (oktaAuth: OktaAuth, userId: string, configId: string) =>
    Backend.deleteScopUser(oktaAuth, userId, configId);

const addScopUser = async (scopUser: ScopUser_new, oktaAuth: OktaAuth) => Backend.addScopUser(scopUser, oktaAuth);

const updateScopUserConfig = async (
    oktaAuth: OktaAuth,
    userId: string,
    oldConfigId: string,
    newConfigId: string,
    new_cohort_typ_id?: string,
    new_cohort_list?: string[],
) => Backend.updateScopUserConfig(oktaAuth, userId, oldConfigId, newConfigId, new_cohort_typ_id, new_cohort_list);
// ********* End backend actions ***********

interface UserManagementProps extends RouteComponentProps {
    progress: Function;
}

const cohortStrToList = (cohortList: string): string[] => {
    if (!cohortList) {
        return null;
    }
    return cohortList.split(',').map((co) => co.trim());
};

// ********** Componants: ************

const AddUserItem = (props: {
    id?: string;
    itemName?: string;
    placeHolder?: string;
    withTitle: boolean;
    inputType: string;
    selectList?: any;
    labelKey?: string;
    setValue?: SetStateAction<any>;
    value?: any;
    isMandatory?: boolean;
    disabled?: boolean;
}) => {
    const {
        id,
        itemName,
        placeHolder,
        withTitle,
        inputType,
        selectList,
        labelKey,
        setValue,
        value,
        isMandatory,
        disabled,
    } = props;

    const onChangeValue = (event: any, newValue: any) => {
        setValue(newValue);
    };

    return (
        <Stack spacing={2} direction="column" className="UmItem" id={id}>
            {withTitle ? (
                <Stack spacing={1} direction="row">
                    <Typography>{itemName}</Typography>
                    {isMandatory ? <Typography sx={{ color: '#ED4D4D', fontWeight: '400' }}>*</Typography> : null}
                </Stack>
            ) : null}
            {inputType === 'text' && (
                <TextField
                    className="ItemInput"
                    disabled={disabled}
                    placeholder={placeHolder}
                    sx={{ '& fieldset': { borderRadius: 2 } }}
                    value={value || ''}
                    onChange={(e) => {
                        setValue(e.target.value);
                    }}
                />
            )}
            {inputType === 'select' && (
                <Autocomplete
                    options={selectList || []}
                    getOptionLabel={(item) => item[labelKey]}
                    disabled={disabled}
                    sx={{ '& fieldset': { borderRadius: 2 } }}
                    value={value}
                    onChange={onChangeValue}
                    renderInput={(params) => <TextField {...params} placeholder={placeHolder} />}
                />
            )}
        </Stack>
    );
};

const AddUserSection = (props: {
    showProgress: Function;
    oktaAuth: OktaAuth;
    userList: UserManagementUserResponse[];
    projectList: UserManagementProjectResponse[];
    connectedUsers: ScopUser_new[];
    setConnectedUsers: SetStateAction<any>;
}) => {
    const { showProgress, oktaAuth, userList, projectList, connectedUsers, setConnectedUsers } = props;

    const [addButtonEnabled, setAddButtonEnabled] = useState<boolean>(false);
    const [cohortEnanled, setCohortEnabled] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>(null);
    const [cohortTypeList, setCohortTypeList] = useState<any[]>([]);

    const [userValue, setUserValue] = useState<UserManagementUserResponse>(null);
    const [configValue, setConfigValue] = useState<UserManagementProjectResponse>(null);
    const [cohortTypeValue, setCohortTypeValue] = useState<UserManagementCohortTypeResponse>(null);
    const [cohortListValue, setCohortListValue] = useState<string>(null);

    useEffect(() => {
        if (validateData()) {
            if (userValue && configValue) {
                setAddButtonEnabled(true);
            } else {
                setAddButtonEnabled(false);
            }
        } else {
            setAddButtonEnabled(false);
        }
    }, [userValue, configValue, cohortTypeValue, cohortListValue]);

    useEffect(() => {
        if (configValue) {
            setCohortEnabled(true);
            setCohortTypeList(configValue.cohort_types);
        } else {
            setCohortEnabled(false);
            setCohortTypeValue(null);
            setCohortListValue('');
        }
    }, [configValue]);

    const clearFields = () => {
        setUserValue(null);
        setConfigValue(null);
        setCohortTypeValue(null);
        setCohortListValue('');
        setCohortEnabled(false);
    };

    const validateData = () => {
        if ((cohortTypeValue && !cohortListValue) || (!cohortTypeValue && cohortListValue)) {
            return false;
        }
        if (userValue) {
            // eslint-disable-next-line guard-for-in
            for (const i in connectedUsers) {
                if (connectedUsers[i].user_id === userValue.user_id && userValue.urole !== 'admin') {
                    setErrorMessage("This user can't be assigned to more then one project");
                    return false;
                }
                if (
                    configValue &&
                    connectedUsers[i].user_id === userValue.user_id &&
                    connectedUsers[i].config_id === configValue.config_id
                ) {
                    setErrorMessage('This user is already assigned to this project');
                    return false;
                }
            }
        }

        setErrorMessage('');
        return true;
    };

    const addUserConfig = () => {
        if (validateData()) {
            const scopUser: ScopUser_new = {
                user_id: userValue.user_id,
                user_name: userValue.user_name,
                config_id: configValue.config_id,
                config_name: configValue.config_name,
                cohort_type: cohortTypeValue,
                cohort_names: cohortStrToList(cohortListValue),
            };
            console.log('showProgress true is in: addUserConfig');
            showProgress(true);
            addScopUser(scopUser, oktaAuth).then((result) => {
                if (result) {
                    getScopUsers(oktaAuth).then((result) => {
                        setConnectedUsers(result.data);
                        showProgress(false);
                        console.log('showProgress false is in: addUserConfig');
                    });
                } else {
                    showProgress(false);
                }
            });

            clearFields();
        }
    };

    return (
        <Stack>
            <Stack className="AddUserBox" direction="row">
                <AddUserItem
                    itemName="Username"
                    placeHolder="Ex.: user.name@aquant.io"
                    withTitle
                    inputType="select"
                    selectList={userList}
                    labelKey="user_name"
                    setValue={setUserValue}
                    value={userValue}
                    isMandatory
                />
                <AddUserItem
                    itemName="SID Project"
                    withTitle
                    inputType="select"
                    selectList={projectList}
                    labelKey="config_name"
                    setValue={setConfigValue}
                    value={configValue}
                    isMandatory
                />
                <AddUserItem
                    itemName="Cohort Type"
                    withTitle
                    inputType="select"
                    selectList={cohortTypeList}
                    labelKey="cohort_label"
                    setValue={setCohortTypeValue}
                    value={cohortTypeValue}
                    disabled={!cohortEnanled}
                />
                <AddUserItem
                    id="UmInputItem"
                    itemName="Cohort Names"
                    placeHolder="Team1, Team2..."
                    withTitle
                    inputType="text"
                    setValue={setCohortListValue}
                    value={cohortListValue}
                    disabled={!cohortEnanled}
                />
                <Stack className="ButtonBox">
                    <Button
                        className="AddUserButton"
                        disabled={!addButtonEnabled}
                        classes={{ disabled: 'AddUserButtonDisabled' }}
                        onClick={addUserConfig}
                        sx={{ borderRadius: 2 }}
                    >
                        Add
                    </Button>
                </Stack>
            </Stack>
            <Stack>{errorMessage ? <Typography className="ErrorMessage">{errorMessage}</Typography> : null}</Stack>
        </Stack>
    );
};

const ScopConnectedUsers = (props: {
    oktaAuth: OktaAuth;
    users: ScopUser_new[];
    showProgress: Function;
    projectList: UserManagementProjectResponse[];
    setUserToDelete: SetStateAction<any>;
    setFlatScopUserList: SetStateAction<any>;
}) => {
    const { oktaAuth, users, showProgress, projectList, setUserToDelete, setFlatScopUserList } = props;

    const [editModeId, setEditModeId] = useState<ScopUser_new>(null);
    const [initialConfigValue, setInitialConfigValue] = useState<UserManagementProjectResponse>(null);
    const [cohortTypeList, setCohortTypeList] = useState<UserManagementCohortTypeResponse[]>([]);

    const [editConfigValue, setEditConfigValue] = useState<UserManagementProjectResponse>(null);
    const [editCohortTypeValue, setEditCohortTypeValue] = useState<UserManagementCohortTypeResponse>(null);
    const [editCohortListValue, setEditCohortListValue] = useState<string>(null);

    const [confirmDisabled, setConfirmDisabled] = useState<boolean>(false);
    const [cohortEnabled, setCohortEnabled] = useState<boolean>(false);

    useEffect(() => {
        if (
            !editConfigValue ||
            (userConfigExist(editModeId.user_id, editConfigValue.config_id) &&
                editConfigValue.config_id !== editModeId.config_id)
        ) {
            setConfirmDisabled(true);
        } else if ((editCohortTypeValue && !editCohortListValue) || (!editCohortTypeValue && editCohortListValue)) {
            setConfirmDisabled(true);
        } else {
            setConfirmDisabled(false);
        }
    }, [editConfigValue, editCohortTypeValue, editCohortListValue]);

    useEffect(() => {
        if (editConfigValue) {
            setCohortEnabled(true);
            if (editConfigValue.config_id !== initialConfigValue.config_id) {
                setEditCohortListValue(null);
                setEditCohortTypeValue(null);
                setInitialConfigValue(editConfigValue);
                setCohortTypeList(editConfigValue.cohort_types);
            }
        } else {
            setCohortEnabled(false);
            setEditCohortTypeValue(null);
            setEditCohortListValue(null);
        }
    }, [editConfigValue]);

    useEffect(() => {}, [editModeId]);

    const userConfigExist = (userId: string, configId: string) => {
        for (const i in users) {
            if (users[i].config_id === configId && users[i].user_id === userId) {
                return true;
            }
        }
        return false;
    };

    const handleEditMode = (row: ScopUser_new) => {
        setEditModeId(row);
        console.log('row: ');
        console.log(row);
        const configObject = projectList.find((project) => project.config_id === row.config_id);
        setCohortTypeList(configObject.cohort_types);
        setInitialConfigValue({
            config_id: row.config_id,
            config_name: row.config_name,
            cohort_types: configObject.cohort_types,
        });
        setEditCohortListValue(row.cohort_names ? row.cohort_names.join(',') : null);
        setEditConfigValue({
            config_id: row.config_id,
            config_name: row.config_name,
            cohort_types: configObject.cohort_types,
        });
        setEditCohortTypeValue(row.cohort_type);
    };

    const handleConfirmChanges = (row: ScopUser_new) => {
        if (
            row.config_id !== editConfigValue.config_id ||
            (row.cohort_type
                ? row.cohort_type.cohort_id
                : editCohortTypeValue !== null
                  ? editCohortTypeValue.cohort_id
                  : null) ||
            (row.cohort_names ? row.cohort_names.join(',') : null) !== editCohortListValue
        ) {
            showProgress(true);
            updateScopUserConfig(
                oktaAuth,
                row.user_id,
                row.config_id,
                editConfigValue.config_id,
                editCohortTypeValue ? editCohortTypeValue.cohort_id : null,
                cohortStrToList(editCohortListValue),
            ).then((result) => {
                if (result) {
                    const index = users.findIndex(
                        (user) => user.config_id === row.config_id && user.user_id === row.user_id,
                    );
                    if (index > -1) {
                        users[index].config_id = editConfigValue.config_id;
                        users[index].config_name = editConfigValue.config_name;
                        users[index].cohort_type = editCohortTypeValue;
                        users[index].cohort_names = editCohortListValue ? editCohortListValue.split(',') : null;
                        const usersList = [...users];
                        setFlatScopUserList(usersList);
                    }
                }
                showProgress(false);
            });
        }
        setEditModeId(null);
    };

    return (
        <Stack className="UsersContainer">
            <TableContainer className="TableContainer" sx={{ backgroundColor: 'white' }}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead sx={{ fontWeight: 500 }}>
                        <TableRow>
                            <TableCell>Username</TableCell>
                            <TableCell>SID Project</TableCell>
                            <TableCell>Cohort</TableCell>
                            <TableCell>Cohort Names</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((row) => (
                            <TableRow
                                className="TableRow"
                                key={row.user_id + row.config_id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell className="TableCell">{row.user_name}</TableCell>
                                <TableCell className="TableCell">
                                    {editModeId &&
                                    row.user_id === editModeId.user_id &&
                                    row.config_id === editModeId.config_id ? (
                                        <AddUserItem
                                            withTitle={false}
                                            inputType="select"
                                            selectList={projectList}
                                            setValue={setEditConfigValue}
                                            value={editConfigValue}
                                            labelKey="config_name"
                                        />
                                    ) : (
                                        row.config_name
                                    )}
                                </TableCell>
                                <TableCell className="TableCell">
                                    {editModeId &&
                                    row.user_id === editModeId.user_id &&
                                    row.config_id === editModeId.config_id ? (
                                        <AddUserItem
                                            withTitle={false}
                                            inputType="select"
                                            selectList={cohortTypeList}
                                            setValue={setEditCohortTypeValue}
                                            value={editCohortTypeValue}
                                            disabled={!cohortEnabled}
                                            labelKey="cohort_label"
                                        />
                                    ) : row.cohort_type ? (
                                        row.cohort_type.cohort_label
                                    ) : (
                                        ''
                                    )}
                                </TableCell>
                                <TableCell className="TableCell" id="TableCellText">
                                    {editModeId &&
                                    row.user_id === editModeId.user_id &&
                                    row.config_id === editModeId.config_id ? (
                                        <AddUserItem
                                            withTitle={false}
                                            setValue={setEditCohortListValue}
                                            value={editCohortListValue}
                                            disabled={!cohortEnabled}
                                            inputType="text"
                                        />
                                    ) : row.cohort_names ? (
                                        row.cohort_names.join(', ')
                                    ) : (
                                        ''
                                    )}
                                </TableCell>
                                {editModeId &&
                                row.user_id === editModeId.user_id &&
                                row.config_id === editModeId.config_id ? (
                                    <TableCell className="TableButton">
                                        <Button
                                            className="ConfirmButton"
                                            classes={{ disabled: 'ConfirmButtonDisabled' }}
                                            disabled={confirmDisabled}
                                            sx={{ border: 1, borderRadius: 2 }}
                                            onClick={() => {
                                                handleConfirmChanges(row);
                                            }}
                                        >
                                            Confirm
                                        </Button>
                                        <Button
                                            className="CancelButton"
                                            sx={{ border: 1, borderRadius: 2 }}
                                            onClick={() => {
                                                setEditModeId(null);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </TableCell>
                                ) : (
                                    <TableCell className="TableButton">
                                        <IconButton
                                            onClick={() => {
                                                handleEditMode(row);
                                            }}
                                        >
                                            <ConfigEditIcon color="black" style={{ cursor: 'pointer' }} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => {
                                                setUserToDelete(row);
                                            }}
                                        >
                                            <DeleteIcon color="black" style={{ cursor: 'pointer' }} />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};

const UserManagement: FunctionComponent<UserManagementProps> = (props: UserManagementProps) => {
    const currentTenant =
        Util.getSessionCurrentTenantInfo() && Util.getSessionCurrentTenantInfo().id
            ? Util.getSessionCurrentTenantInfo()
            : Util.getCurrentTenantInfo();
    const { authState, oktaAuth } = useOktaAuth();

    const [userList, setUserList] = useState<UserManagementUserResponse[] | []>(null);
    const [projectList, setProjectList] = useState<UserManagementProjectResponse[] | []>(null);

    const [flatScopUserList, setFlatScopUserList] = useState<ScopUser_new[]>([]);
    const [userToDelete, setUserToDelete] = useState<ScopUser_new>(null);

    useEffect(() => {
        // set project lst, user lst, connected users lst
        if (!authState) {
            return;
        }

        if (!authState.isAuthenticated) {
            setUserList([]);
        } else {
            props.progress(true);
            getUsersAndProjects(oktaAuth).then((result) => {
                setUserList(result.data.users);
                setProjectList(result.data.projects);

                getScopUsers(oktaAuth).then((result) => {
                    setFlatScopUserList(result.data);
                    props.progress(false);
                });
            });
        }
    }, [authState, oktaAuth]);

    const DeleteUserConfigDialog = (props: { userToDelete: ScopUser_new; showProgress: Function }) => {
        const { userToDelete, showProgress } = props;

        const deleteUserConfig = (userToDelete: ScopUser_new) => {
            setUserToDelete(null);
            showProgress(true);
            deleteScopUser(oktaAuth, userToDelete.user_id, userToDelete.config_id).then((result) => {
                if (result.data) {
                    for (let i = 0; i < flatScopUserList.length; i++) {
                        if (
                            flatScopUserList[i].user_id === userToDelete.user_id &&
                            flatScopUserList[i].config_id === userToDelete.config_id
                        ) {
                            flatScopUserList.splice(i, 1);
                            setFlatScopUserList(flatScopUserList);
                        }
                    }
                    console.log(`user ${userToDelete.user_name}with schema ${userToDelete.config_name} deleted!`);
                } else {
                    console.log(
                        `user ${userToDelete.user_name}with schema ${userToDelete.config_name} was not deleted!`,
                    );
                }
                showProgress(false);
            });
        };

        return (
            <SiDialog open={userToDelete !== null} onClose={() => setUserToDelete(null)}>
                <DialogTitle>
                    <Stack className="DialogTitle">
                        <Typography className="DialogTitleText">Are you sure?</Typography>
                        <img
                            src={AquantCancelIcon}
                            onClick={() => {
                                setUserToDelete(null);
                            }}
                            alt="cancel icon"
                            className="CancelIcon"
                        />
                    </Stack>
                </DialogTitle>
                <Divider />
                <Paper className="DialogContentPaper">
                    <DialogContent className="ProcessDialogContent">
                        <DialogContentText className="DialogContentTextNormal">
                            You are about to delete the user "{userToDelete.user_name}". This user won't have access
                            anymore to Service Co-Pilot.
                        </DialogContentText>
                        <DialogContentText className="DialogContentTextBold">
                            Are you sure you want to proceed?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUserToDelete(null)} disableRipple className="DialogActionBtn">
                            Discard
                        </Button>
                        <Button
                            onClick={() => deleteUserConfig(userToDelete)}
                            variant="contained"
                            disableRipple
                            className="DialogActionBtnContained"
                        >
                            Yes
                        </Button>
                    </DialogActions>
                </Paper>
            </SiDialog>
        );
    };

    return (
        <Stack>
            <Stack spacing={2} direction="column" className="UserManagement">
                <Stack className="UserManagementHeader" direction="row">
                    <Breadcrumbs className="HeaderBreadCrumbUm">
                        {currentTenant.logo !== 'null' ? (
                            <img src={currentTenant.logo} className="BreadcrumbsLogo" alt="tenant-logo" />
                        ) : (
                            <Typography>{currentTenant.name}</Typography>
                        )}
                    </Breadcrumbs>
                </Stack>

                <Stack className="UserManagementContainer">
                    <Stack className="AddUserContainer">
                        <Typography className="UserManagementTitles">Add new users</Typography>
                        <AddUserSection
                            showProgress={props.progress}
                            oktaAuth={oktaAuth}
                            userList={userList}
                            projectList={projectList}
                            connectedUsers={flatScopUserList}
                            setConnectedUsers={setFlatScopUserList}
                        />
                    </Stack>

                    <Stack className="AddUserContainer">
                        {flatScopUserList.length === 0 && (
                            <Typography className="UserManagementTitles">No user has been added yet!</Typography>
                        )}
                        {flatScopUserList.length === 1 && (
                            <Typography className="UserManagementTitles">{flatScopUserList.length} User</Typography>
                        )}
                        {flatScopUserList.length > 1 && (
                            <Typography className="UserManagementTitles">{flatScopUserList.length} Users</Typography>
                        )}
                        {flatScopUserList.length > 0 && (
                            <ScopConnectedUsers
                                oktaAuth={oktaAuth}
                                users={flatScopUserList}
                                showProgress={props.progress}
                                projectList={projectList}
                                setUserToDelete={setUserToDelete}
                                setFlatScopUserList={setFlatScopUserList}
                            />
                        )}
                    </Stack>

                    {userToDelete ? (
                        <DeleteUserConfigDialog userToDelete={userToDelete} showProgress={props.progress} />
                    ) : null}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default UserManagement;
